import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthWidgetComponent } from '../widgets/auth/auth-widget.component';
import { AuthService } from '../core/auth.service';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';
import { SellerOnboardingField, SellerOnboardingPayload, SellerOnboardingSaveError, SellerOnboardingService, SellerOnboardingStep, SellerOnboardingStepKey } from '../widgets/seller-onboarding/seller-onboarding.service';
import { SiteConfigService } from '../core/site-config.service';
import { DynamicFormField, DynamicFormFieldsComponent } from '../widgets/forms/dynamic-form-fields.component';
import { ProductQuickEditorComponent } from '../widgets/product-quick-editor/product-quick-editor.component';
import { SellerPanelService } from '../widgets/seller-panel/seller-panel.service';
import { FortuneCategoryService, FortuneSaleCategory } from '../widgets/fortune-category/fortune-category.service';

interface InstantOfferDraft {
  offerID?: number;
  categoryID: number;
  description: string;
  creditPrice: number | null;
  active: boolean;
}

@Component({
  selector: 'app-seller-onboarding-page',
  imports: [AuthWidgetComponent, DynamicFormFieldsComponent, FormsModule, ProductQuickEditorComponent, RouterLink],
  template: `
    <section class="become-reader-page">
      @if (instantOnboarding()) {
        <article class="instant-reader">
          @if (!auth.loggedIn()) {
            <p class="eyebrow">FALCI OL</p>
            <h1>Giriş yap, falcı profilini aç.</h1>
            <p>Giriş yaptıktan sonra yalnızca mağaza adını seçmen yeterli.</p>
            <app-auth-widget />
          } @else if (instantStage() === 'name') {
            <p class="eyebrow">FALCI HESABI</p>
            <h1>Mağazanı aç.</h1>
            <p>Mağaza adını yaz. Hesabın hemen açılacak.</p>
            @if (error()) { <p class="notice error">{{error()}}</p> }
            <form class="instant-form" (ngSubmit)="startInstantOnboarding()">
              <label><span>Mağaza adı</span><input name="instantShopName" [(ngModel)]="instantShopName" minlength="2" maxlength="100" pattern="[A-Za-z0-9._-]+" autocomplete="organization" placeholder="Örnek: Falci_Zeynep" required/><small>Latin harfleri, rakam, nokta, tire ve alt çizgi kullan.</small></label>
              <button type="submit" class="primary" [disabled]="saving()">{{saving()?'Mağazan açılıyor…':'Mağazamı aç'}}</button>
            </form>
          } @else {
            <p class="eyebrow">MAĞAZAN AÇIK</p>
            <h1>Profilini hazırla.</h1>
            <p>Fotoğrafını seç, bakacağın fal türlerini aç ve fiyatlarını belirle. Kaydettiğin fallar hemen yayına girer.</p>
            @if (error()) { <p class="notice error">{{error()}}</p> }
            @if (notice()) { <p class="notice success">{{notice()}}</p> }
            <section class="instant-setup">
              <label class="instant-photo"><span>Profil resmi <small>(isteğe bağlı)</small></span><input type="file" accept="image/jpeg,image/png,image/webp" (change)="selectInstantProfileImage($event)"/>@if(instantProfilePreview()){<img [src]="instantProfilePreview()" alt="Seçilen profil resmi"/>}@else{<b>Resim seç</b>}</label>
              <div class="instant-category-head"><div><strong>Bakacağın fallar</strong><small>Satışa açacağın her fal için kısa bir açıklama ve kredi fiyatı seç.</small></div><span>{{instantActiveCategoryCount()}} fal açık</span></div>
              @if (instantCategoriesLoading()) {
                <p>Kategoriler hazırlanıyor…</p>
              } @else {
                <div class="instant-category-list">
                  @for (category of instantCategories(); track category.categoryID) {
                    <article [class.active]="instantDraft(category.categoryID).active">
                      <label class="instant-switch"><input type="checkbox" [(ngModel)]="instantDraft(category.categoryID).active"/><span></span><b>{{category.title}}</b></label>
                      <label><span>Fal açıklaması</span><textarea maxlength="250" [(ngModel)]="instantDraft(category.categoryID).description" [disabled]="!instantDraft(category.categoryID).active" placeholder="Bu falda nasıl yorum yaptığını kısaca anlat"></textarea><small>{{instantDraft(category.categoryID).description.length}} / 250</small></label>
                      <label><span>Fiyat</span><select [(ngModel)]="instantDraft(category.categoryID).creditPrice" [disabled]="!instantDraft(category.categoryID).active"><option [ngValue]="null">Kredi seç</option>@for (price of instantPriceOptions(category); track price) {<option [ngValue]="price">{{price}} kredi</option>}</select></label>
                    </article>
                  } @empty {
                    <p>Satışa açılabilecek fal kategorisi bulunamadı.</p>
                  }
                </div>
              }
              <button type="button" class="primary" [disabled]="saving() || instantCategoriesLoading()" (click)="publishInstantSeller()">{{saving()?'Yayınlanıyor…':'Seçtiğim falları yayınla'}}</button>
              <a class="instant-panel-link" [routerLink]="i18n.routePath('sellerDashboard') + '/fortune-sales'">Falcı paneline git</a>
            </section>
          }
        </article>
      } @else if (!auth.loggedIn() && !fortuneBrand()) {
        <article class="reader-landing commerce-landing">
          <div class="landing-hero">
            <div class="landing-copy">
              <p class="eyebrow">{{ site.config().name }} satıcı başvurusu</p>
              <h1>Ürettiklerini güçlü bir dijital mağazaya dönüştür.</h1>
              <p class="lead">El emeği, tasarım ve özgün ürünlerini güvenli ödeme, güçlü SEO ve kolay mağaza yönetimiyle doğrudan müşterilerine ulaştır.</p>
              <div class="benefits" aria-label="Pazaryeri satıcı avantajları">
                <span>Kendi mağaza vitrinin</span><span>Güvenli ödeme altyapısı</span><span>Ürün ve sipariş yönetimi</span><span>SEO uyumlu ürün sayfaları</span>
              </div>
            </div>
            <div class="auth-card">
              <small>Mağazanı aç</small><h2>Satıcı olmak için giriş yap</h2>
              <p>Aymudo hesabınla devam et. Hesabın yoksa aynı ekrandan kayıt olup başvurunu başlatabilirsin.</p>
              <app-auth-widget />
            </div>
          </div>
          <section class="earning-strip">
            <div><strong>Ürünlerini yayınla</strong><span>Görsel, seçenek, stok ve fiyatlarını kolayca yönet.</span></div>
            <div><strong>Markanı büyüt</strong><span>Mağazan ve ürünlerin arama motorlarına uygun kalıcı sayfalarda yer alsın.</span></div>
            <div><strong>Siparişlerini yönet</strong><span>Satış, mesaj, kampanya ve kazanç akışını tek panelden takip et.</span></div>
          </section>
        </article>
      } @else if (!auth.loggedIn() && i18n.lang() !== 'tr') {
        <article class="reader-landing">
          <div class="landing-hero">
            <div class="landing-copy">
              <p class="eyebrow">{{ localizedLanding().eyebrow }}</p>
              <h1>{{ localizedLanding().title }}</h1>
              <p class="lead">{{ localizedLanding().lead }}</p>
              <div class="benefits">
                @for (benefit of localizedLanding().benefits; track benefit) { <span>{{ benefit }}</span> }
              </div>
            </div>
            <div class="auth-card">
              <small>{{ localizedLanding().start }}</small>
              <h2>{{ localizedLanding().authTitle }}</h2>
              <p>{{ localizedLanding().authLead }}</p>
              <app-auth-widget />
            </div>
          </div>
          <section class="earning-strip">
            @for (item of localizedLanding().highlights; track item.title) {
              <div><strong>{{ item.title }}</strong><span>{{ item.text }}</span></div>
            }
          </section>
          <section class="seo-content">
            <div>
              <p class="eyebrow">{{ localizedLanding().why }}</p>
              <h2>{{ localizedLanding().whyTitle }}</h2>
              <p>{{ localizedLanding().whyText }}</p>
            </div>
            <aside class="keyword-card">
              <h3>{{ localizedLanding().stepsTitle }}</h3>
              <ol>
                @for (step of localizedLanding().steps; track step) { <li>{{ step }}</li> }
              </ol>
            </aside>
          </section>
          <section class="internal-links">
            <a [routerLink]="i18n.routePath('fortune')">{{ i18n.t('getFortune') }}</a>
            <a [routerLink]="i18n.routePath('sellers')">{{ i18n.t('allReaders') }}</a>
            <a [routerLink]="i18n.routePath('support')">{{ i18n.t('support') }}</a>
          </section>
        </article>
      } @else if (!auth.loggedIn()) {
        <article class="reader-landing">
          <div class="landing-hero">
            <div class="landing-copy">
              <p class="eyebrow">Fal Deryası falcı başvurusu</p>
              <h1>İnternetten fal bakarak para kazanmak isteyen falcılar için profesyonel vitrin.</h1>
              <p class="lead">
                Kahve falı, tarot, rüya yorumu, astroloji veya enerji okuması yapıyorsan Fal Deryası'nda online falcı
                profili açabilir; fal severlere ulaşan, SEO uyumlu ve güvenli ödeme altyapısı olan bir dijital dükkana sahip olabilirsin.
              </p>
              <div class="benefits" aria-label="Fal Deryası falcı avantajları">
                <span>Evden gelir elde etme fırsatı</span>
                <span>Canlı yayın ve hediye altyapısı</span>
                <span>Kredi ile güvenli ödeme</span>
                <span>SEO uyumlu falcı profili</span>
              </div>
            </div>
            <div class="auth-card">
              <small>Başvurunu başlat</small>
              <h2>Falcı olmak için giriş yap</h2>
              <p>Aymudo hesabınla güvenli şekilde devam et. Hesabın yoksa aynı ekrandan kayıt olabilir ve başvuru adımlarına geçebilirsin.</p>
              <app-auth-widget />
            </div>
          </div>

          <section class="earning-strip" aria-label="Online falcı kazanç modeli">
            <div>
              <strong>Online fal bak</strong>
              <span>Kahve falı, tarot, rüya yorumu ve enerji danışmanlığı hizmetlerini dijital vitrinde yayınla.</span>
            </div>
            <div>
              <strong>Takipçi kitleni büyüt</strong>
              <span>Falcı profilin, ürünlerin, yorumların ve canlı yayınların arama motorları için düzenli sunulur.</span>
            </div>
            <div>
              <strong>Gelirini yönet</strong>
              <span>Kazanç performansı; profil kalitesi, talep, hizmet fiyatı ve müşteri memnuniyetine göre değişir.</span>
            </div>
          </section>

          <section class="seo-content">
            <div>
              <p class="eyebrow">Neden Fal Deryası?</p>
              <h2>Online falcı olmak isteyenler için tek yerde profil, ödeme, yorum ve canlı yayın deneyimi.</h2>
              <p>
                İnternet üzerinden para kazanmak isteyen falcılar için yalnızca bir başvuru formu değil; görünürlük,
                güven, müşteri ilişkisi ve satış akışını birlikte düşünen bir platform hazırlıyoruz. Fal Deryası'nda
                falcı olma süreci tamamlandığında kendi profil sayfan, sunduğun fal hizmetleri, müşteri yorumların,
                canlı yayınların ve takipçi kitlen tek bir marka deneyimi içinde buluşur.
              </p>
              <p>
                Amaç, yeteneğini tesadüfi sosyal medya mesajlarına sıkıştırmak yerine; fal baktırmak isteyen kullanıcıların
                seni bulabildiği, hizmetlerini karşılaştırabildiği ve güvenli şekilde ödeme yapabildiği kalıcı bir dijital
                vitrin oluşturmaktır.
              </p>
            </div>
            <aside class="keyword-card">
              <h3>Bu sayfa kimler için?</h3>
              <ul>
                <li>Evden para kazanmak isteyen kahve falı yorumcuları</li>
                <li>Online tarot falı bakmak isteyen danışmanlar</li>
                <li>Rüya tabiri, enerji okuması veya astroloji yorumu yapanlar</li>
                <li>Kendi müşteri kitlesini büyütmek isteyen profesyonel falcılar</li>
              </ul>
            </aside>
          </section>

          <section class="how-it-works">
            <p class="eyebrow">Nasıl çalışır?</p>
            <h2>Falcı başvurusu üç net adımda tamamlanır.</h2>
            <div class="steps-grid">
              <div>
                <small>01</small>
                <h3>Hesap oluştur</h3>
                <p>Güvenli Aymudo hesabınla giriş yap veya kayıt ol. Fal Deryası profilin bu hesap üzerinden yönetilir.</p>
              </div>
              <div>
                <small>02</small>
                <h3>Falcı profilini hazırla</h3>
                <p>Uzmanlık alanlarını, profil bilgilerini, hizmet açıklamalarını ve gerekli başvuru alanlarını doldur.</p>
              </div>
              <div>
                <small>03</small>
                <h3>Gelir akışını başlat</h3>
                <p>Profilin onaylandıktan sonra fal hizmetlerini yayınla, takipçi kazan, canlı yayın aç ve yorumlarını büyüt.</p>
              </div>
            </div>
          </section>

          <section class="internal-links" aria-label="Fal Deryası içinde keşfet">
            <a [routerLink]="i18n.routePath('fortune')">Fal kategorilerini incele</a>
            <a [routerLink]="i18n.routePath('sellers')">Falcı profillerini gör</a>
            <a [routerLink]="i18n.routePath('support')">Destek merkezine git</a>
          </section>

          <section class="faq-preview">
            <p class="eyebrow">Sık sorulan sorular</p>
            <h2>Falcı olarak internetten para kazanma hakkında merak edilenler.</h2>
            <details open>
              <summary>Fal Deryası'nda falcı olmak ücretli mi?</summary>
              <p>Başvuru akışı platform tarafından yönetilir. Yayına alma, komisyon ve ödeme koşulları başvuru sürecinde güncel kurallara göre gösterilir.</p>
            </details>
            <details>
              <summary>Online fal bakarak ne kadar kazanabilirim?</summary>
              <p>Kazanç garanti edilmez; profil görünürlüğü, hizmet fiyatı, müşteri talebi, yorum kalitesi ve canlı yayın etkileşimine göre değişir.</p>
            </details>
            <details>
              <summary>Hangi fal türleriyle başvuru yapabilirim?</summary>
              <p>Kahve falı, tarot, rüya yorumu, astroloji, enerji okuması ve platformun desteklediği diğer danışmanlık alanlarıyla başvuru yapabilirsin.</p>
            </details>
          </section>
        </article>
      } @else if (isSeller() && !postOnboardingStep()) {
        <article class="already-reader">
          <p class="eyebrow">{{ fortuneBrand() ? 'Falcı hesabın hazır' : 'Satıcı hesabın hazır' }}</p>
          <h1>{{ auth.user()?.seller?.shopName || (fortuneBrand() ? 'Falcı profilin' : 'Mağazan') }} zaten aktif.</h1>
          <p>{{ fortuneBrand() ? 'Falcı profilini ve hizmetlerini panelinden yönetebilirsin.' : 'Mağazanı, ürünlerini ve siparişlerini satıcı panelinden yönetebilirsin.' }}</p>
          <a [routerLink]="i18n.routePath('accountProfile')">Hesabıma dön</a>
        </article>
      } @else {
        <article class="onboarding-shell">
          <header class="page-title">
            <p class="eyebrow">{{ onboardingCopy().eyebrow }}</p>
            <h1>{{ onboardingCopy().title }}</h1>
            <p>{{ onboardingCopy().lead }}</p>
          </header>

          @if (loading()) {
            <div class="step-skeleton"><i></i><i></i><i></i></div>
          } @else {
            @if (postOnboardingStep()) {
              <section class="post-onboarding-card">
                <header class="post-onboarding-head">
                  <p class="eyebrow">{{ postOnboardingStep() === 'profileImage' ? 'Profilini tamamla' : 'İlk falını yayınla' }}</p>
                  <h2>{{ postOnboardingStep() === 'profileImage' ? 'Profil fotoğrafını ekle' : 'İlk ürününü satışa aç' }}</h2>
                  <p>{{ postOnboardingStep() === 'profileImage' ? 'Profil fotoğrafı eklemek müşterilerin seni daha kolay tanımasına yardımcı olur. İstersen bu adımı atlayabilirsin.' : ('Hazır ürün şablonlarını kullanmadan, satıcı panelindeki aynı hızlı ürün ekleme ekranıyla ' + (fortuneBrand() ? 'ilk falını' : 'ilk ürününü') + ' yayınla.') }}</p>
                </header>
                @if (postOnboardingStep() === 'profileImage') {
                  <div class="post-profile-upload">
                    @if (postProfilePreview()) { <img [src]="postProfilePreview()" alt="Profil önizlemesi" /> }
                    <label class="post-upload-button">
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" [disabled]="postProfileUploading()" (change)="selectPostProfileImage($event)" />
                      {{ postProfileUploading() ? 'Görsel yükleniyor…' : (postProfileUpload() ? 'Görsel seçildi' : 'Profil görseli seç') }}
                    </label>
                  </div>
                  @if (postProfileError()) { <p class="notice error">{{ postProfileError() }}</p> }
                  <footer class="step-actions">
                    <button type="button" class="ghost" (click)="skipPostStep()" [disabled]="saving() || postProfileUploading()">Şimdilik geç</button>
                    <button type="button" class="primary" (click)="savePostProfileImage()" [disabled]="saving() || postProfileUploading() || !postProfileUpload()">Kaydet ve devam et</button>
                  </footer>
                } @else {
                  <app-product-quick-editor mode="listing" [startWithList]="true" [listOnly]="true" [navigateTemplateToPanel]="true" (saved)="onFirstProductSaved()" />
                  <footer class="step-actions post-product-actions">
                    <button type="button" class="ghost" (click)="skipPostStep()" [disabled]="saving()">Ürün eklemeden geç</button>
                  </footer>
                }
              </section>
            } @else {
            <div class="step-layout">
              <aside class="steps-nav">
                @for (step of steps(); track step.stepKey) {
                  <button
                    type="button"
                    [class.active]="currentStepKey() === step.stepKey"
                    [class.done]="isCompleted(step.stepKey)"
                    (click)="openStep(step.stepKey)"
                  >
                    <small>{{ step.displayOrder || stepIndex(step.stepKey) + 1 }}</small>
                    <span>{{ stepTitle(step) }}</span>
                  </button>
                }
              </aside>

              <form class="step-card" (ngSubmit)="saveCurrentStep()">
                @if (notice()) {
                  <p class="notice success">{{ notice() }}</p>
                }
                @if (error()) {
                  <p class="notice error">{{ error() }}</p>
                }

                <div class="step-head">
                  <span>{{ stepIndex(currentStepKey()) + 1 }} / {{ steps().length || stepOrder.length }}</span>
                  <h2>{{ stepTitle(currentStep()) }}</h2>
                  <p>{{ currentStep()?.description || stepDescription(currentStepKey()) }}</p>
                </div>

                @if (currentStepKey() === 'review') {
                  <div class="review-grid">
                    @for (entry of reviewEntries(); track entry.key) {
                      <div>
                        <small>{{ entry.label }}</small>
                        <strong>{{ entry.value }}</strong>
                      </div>
                    }
                  </div>
                } @else {
                  <app-dynamic-form-fields
                    [fields]="currentFields()"
                    [model]="formData"
                    [errors]="fieldErrors()"
                    [hints]="formHints()"
                    (fieldBlur)="onFormFieldBlur($event)"
                    (uploading)="uploadingField.set($event)"
                    (uploadComplete)="onFormUploadComplete($event)"
                    (uploadError)="onFormUploadError($event)"
                  />
                }

                <footer class="step-actions">
                  <button type="button" class="ghost back-action" (click)="previousStep()" [disabled]="saving() || currentStepKey() === firstStepKey()">{{ onboardingCopy().back }}</button>
                  @if (currentStepKey() === 'review') {
                    <button type="button" class="primary" (click)="complete()" [disabled]="saving()">{{ onboardingCopy().complete }}</button>
                  } @else {
                    <button type="submit" class="primary" [disabled]="saving() || !!uploadingField()">
                      {{ saving() ? onboardingCopy().saving : (uploadingField() ? onboardingCopy().uploading : onboardingCopy().continue) }}
                    </button>
                  }
                </footer>
              </form>
            </div>
            }
          }
        </article>
      }
    </section>
  `,
  styles: [`
    .become-reader-page {
      margin: 0 auto;
      max-width: 1180px;
    }
    .instant-reader{background:var(--surface,#fff);border:1px solid var(--border);margin:50px auto;max-width:920px;padding:28px}.instant-reader h1{font-size:clamp(34px,7vw,58px);margin:8px 0 14px}.instant-reader>p{line-height:1.6}.instant-reader .primary{cursor:pointer;margin-top:14px}.instant-form,.instant-setup{display:grid;gap:16px;margin-top:22px}.instant-form{max-width:560px}.instant-form label,.instant-setup label{display:grid;gap:7px}.instant-form label>span,.instant-setup label>span{font-weight:800}.instant-form input,.instant-setup input,.instant-setup select,.instant-setup textarea{background:#fff;border:1px solid #999;color:#222;font:inherit;padding:12px}.instant-photo{border:1px dashed #777;cursor:pointer;padding:14px}.instant-photo input{padding:0}.instant-photo img{height:120px;object-fit:cover;width:120px}.instant-photo b{color:var(--accent)}.instant-category-head{align-items:center;border-top:1px solid var(--border);display:flex;gap:14px;justify-content:space-between;padding-top:18px}.instant-category-head>div{display:grid;gap:4px}.instant-category-head>div>strong{font-size:22px}.instant-category-head small,.instant-category-list small{color:var(--muted)}.instant-category-head>span{background:#eee;border:1px solid #aaa;font-weight:800;padding:8px 10px;white-space:nowrap}.instant-category-list{display:grid;gap:12px}.instant-category-list article{align-items:end;background:#f4f4f4;border:1px solid #aaa;display:grid;gap:12px;grid-template-columns:minmax(150px,.65fr) minmax(260px,1.5fr) 150px;padding:14px}.instant-category-list article.active{background:#fff;border-color:var(--accent)}.instant-category-list textarea{min-height:74px;resize:vertical}.instant-switch{align-items:center!important;display:flex!important;gap:9px!important}.instant-switch input{height:20px;width:20px}.instant-panel-link{color:var(--accent);font-weight:800;justify-self:start}.notice.success{background:#eef9f1;border-color:#68a875;color:#185d2b}@media(max-width:720px){.instant-category-list article{align-items:stretch;grid-template-columns:1fr}.instant-category-head{align-items:flex-start;flex-direction:column}.instant-reader{margin:20px auto;padding:18px}}
    .shop-name-state {
      color: #b42b55;
      display: block;
      font-style: normal;
      font-weight: 800;
      margin-top: 6px;
    }
    .post-onboarding-card {
      background: var(--surface, #fff);
      border: 1px solid var(--border);
      border-radius: 28px;
      display: grid;
      gap: 24px;
      padding: clamp(20px, 4vw, 36px);
    }
    .post-onboarding-head { display: grid; gap: 8px; }
    .post-onboarding-head h2 { font-family: Georgia, serif; font-size: clamp(2rem, 5vw, 4rem); line-height: .95; margin: 0; }
    .post-onboarding-head p:last-child { color: var(--muted); line-height: 1.6; margin: 0; max-width: 760px; }
    .post-profile-upload { align-items: center; display: flex; flex-wrap: wrap; gap: 20px; }
    .post-profile-upload img { border: 1px solid var(--border); border-radius: 50%; height: 150px; object-fit: cover; width: 150px; }
    .post-upload-button { background: var(--accent); border-radius: 999px; color: var(--accent-contrast); cursor: pointer; font-weight: 900; padding: 13px 18px; }
    .post-upload-button input { display: none; }
    .post-product-actions { border-top: 1px solid var(--border); padding-top: 18px; }
    .post-onboarding-card > app-product-quick-editor { display: block; }
    .shop-name-state.valid { color: #1b7a53; }
    .field-error {
      background: rgb(220 59 99 / 12%);
      border: 1px solid rgb(220 59 99 / 24%);
      border-radius: 12px;
      color: #b42b55;
      display: block;
      font-style: normal;
      font-weight: 850;
      padding: 9px 11px;
    }
    .upload-state {
      align-items: center;
      color: var(--accent);
      display: flex;
      font-style: normal;
      gap: 8px;
    }
    .upload-state i {
      animation: pulse 900ms ease-in-out infinite;
      background: currentColor;
      border-radius: 50%;
      height: 9px;
      width: 9px;
    }
    .uploaded-files {
      display: grid;
      gap: 8px;
    }
    .uploaded-files span {
      align-items: center;
      background: color-mix(in srgb, var(--paper) 72%, transparent);
      border: 1px solid var(--border);
      border-radius: 16px;
      display: grid;
      gap: 2px 10px;
      grid-template-columns: auto 1fr;
      padding: 9px;
    }
    .uploaded-files img {
      border-radius: 10px;
      grid-row: span 2;
      height: 46px;
      object-fit: cover;
      width: 46px;
    }
    .uploaded-files small { color: #1b7a53; }

    .reader-landing,
    .already-reader,
    .onboarding-shell {
      background:
        radial-gradient(circle at 12% 8%, color-mix(in srgb, var(--accent) 20%, transparent), transparent 34%),
        linear-gradient(135deg, color-mix(in srgb, var(--surface) 94%, transparent), color-mix(in srgb, var(--paper) 82%, transparent));
      border: 1px solid var(--border);
      border-radius: 38px;
      box-shadow: 0 28px 82px color-mix(in srgb, var(--shadow) 58%, transparent);
      padding: clamp(22px, 4vw, 44px);
    }

    .reader-landing {
      display: grid;
      gap: clamp(20px, 4vw, 34px);
    }

    .landing-hero {
      display: grid;
      gap: 28px;
      grid-template-columns: minmax(0, 1.1fr) minmax(320px, .9fr);
    }

    .landing-copy h1,
    .already-reader h1,
    .page-title h1 {
      color: var(--ink);
      font-size: clamp(2.2rem, 6vw, 5rem);
      letter-spacing: -0.075em;
      line-height: .9;
      margin: 0 0 16px;
    }

    .landing-copy p,
    .already-reader p,
    .page-title p,
    .step-head p {
      color: var(--muted);
      font-size: 1rem;
      line-height: 1.7;
    }

    .landing-copy .lead {
      color: color-mix(in srgb, var(--ink) 76%, var(--muted));
      font-size: clamp(1.05rem, 2vw, 1.25rem);
      max-width: 760px;
    }

    .benefits {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 22px;
    }

    .benefits span,
    .eyebrow {
      background: color-mix(in srgb, var(--accent) 13%, transparent);
      border: 1px solid color-mix(in srgb, var(--accent) 28%, var(--border));
      border-radius: 999px;
      color: var(--accent);
      display: inline-flex;
      font-size: 12px;
      font-weight: 950;
      padding: 8px 12px;
    }

    .auth-card {
      background: color-mix(in srgb, var(--surface) 92%, transparent);
      border: 1px solid var(--border);
      border-radius: 30px;
      padding: 22px;
      position: relative;
      overflow: hidden;
    }

    .auth-card::before {
      background:
        radial-gradient(circle, color-mix(in srgb, var(--accent) 30%, transparent), transparent 62%),
        linear-gradient(135deg, color-mix(in srgb, var(--paper) 26%, transparent), transparent);
      content: '';
      height: 220px;
      position: absolute;
      right: -90px;
      top: -120px;
      width: 220px;
      z-index: 0;
    }

    .auth-card > * {
      position: relative;
      z-index: 1;
    }

    .auth-card small {
      color: var(--accent);
      font-weight: 950;
      letter-spacing: .08em;
      text-transform: uppercase;
    }

    .auth-card h2 {
      color: var(--ink);
      font-size: clamp(1.7rem, 3vw, 2.5rem);
      letter-spacing: -0.055em;
      line-height: 1;
      margin: 10px 0;
    }

    .already-reader a,
    .step-actions button,
    .auth-card {
      box-shadow: 0 16px 42px color-mix(in srgb, var(--shadow) 38%, transparent);
    }

    .already-reader a {
      background: var(--ink);
      border-radius: 999px;
      color: var(--paper);
      display: inline-flex;
      font-weight: 950;
      margin-top: 18px;
      padding: 13px 18px;
      text-decoration: none;
    }

    .earning-strip {
      display: grid;
      gap: 14px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .earning-strip div,
    .keyword-card,
    .steps-grid div,
    .faq-preview details {
      background: color-mix(in srgb, var(--surface) 88%, transparent);
      border: 1px solid var(--border);
      border-radius: 26px;
      box-shadow: 0 14px 34px color-mix(in srgb, var(--shadow) 26%, transparent);
      padding: 18px;
    }

    .earning-strip strong,
    .keyword-card h3,
    .steps-grid h3,
    .faq-preview summary {
      color: var(--ink);
      display: block;
      font-weight: 950;
    }

    .earning-strip span,
    .keyword-card li,
    .steps-grid p,
    .faq-preview p,
    .seo-content p {
      color: var(--muted);
      line-height: 1.65;
    }

    .seo-content {
      align-items: start;
      display: grid;
      gap: 24px;
      grid-template-columns: minmax(0, 1fr) 360px;
    }

    .seo-content h2,
    .how-it-works h2,
    .faq-preview h2 {
      color: var(--ink);
      font-size: clamp(1.9rem, 4vw, 3.4rem);
      letter-spacing: -0.065em;
      line-height: .98;
      margin: 14px 0;
    }

    .keyword-card ul {
      display: grid;
      gap: 12px;
      list-style: none;
      margin: 16px 0 0;
      padding: 0;
    }

    .keyword-card li {
      border-top: 1px solid var(--border);
      padding-top: 12px;
    }

    .steps-grid {
      display: grid;
      gap: 14px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      margin-top: 18px;
    }

    .steps-grid small {
      align-items: center;
      background: var(--ink);
      border-radius: 16px;
      color: var(--paper);
      display: inline-flex;
      font-weight: 950;
      height: 42px;
      justify-content: center;
      margin-bottom: 14px;
      width: 42px;
    }

    .internal-links {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }

    .internal-links a {
      background: color-mix(in srgb, var(--accent) 14%, var(--surface));
      border: 1px solid color-mix(in srgb, var(--accent) 34%, var(--border));
      border-radius: 999px;
      color: var(--ink);
      font-weight: 950;
      padding: 12px 16px;
      text-decoration: none;
    }

    .faq-preview details {
      margin-top: 12px;
    }

    .faq-preview summary {
      cursor: pointer;
      font-size: 1.05rem;
    }

    .faq-preview p {
      margin-bottom: 0;
    }

    .step-layout {
      display: grid;
      gap: 18px;
      grid-template-columns: 280px minmax(0, 1fr);
      margin-top: 28px;
    }

    .steps-nav {
      display: grid;
      gap: 10px;
      align-self: start;
      position: sticky;
      top: 104px;
    }

    .steps-nav button {
      align-items: center;
      background: color-mix(in srgb, var(--surface) 86%, transparent);
      border: 1px solid var(--border);
      border-radius: 22px;
      color: var(--ink);
      cursor: pointer;
      display: grid;
      gap: 12px;
      grid-template-columns: auto 1fr;
      padding: 14px;
      text-align: left;
    }

    .steps-nav small {
      align-items: center;
      background: color-mix(in srgb, var(--paper) 70%, transparent);
      border-radius: 14px;
      display: inline-flex;
      font-weight: 950;
      height: 36px;
      justify-content: center;
      width: 36px;
    }

    .steps-nav button.active,
    .steps-nav button.done {
      border-color: color-mix(in srgb, var(--accent) 42%, var(--border));
    }

    .steps-nav button.active small {
      background: var(--ink);
      color: var(--paper);
    }

    .step-card {
      background: color-mix(in srgb, var(--surface) 90%, transparent);
      border: 1px solid var(--border);
      border-radius: 30px;
      padding: clamp(18px, 3vw, 30px);
    }

    .step-head span {
      color: var(--accent);
      font-weight: 950;
    }

    .step-head h2 {
      color: var(--ink);
      font-size: clamp(1.8rem, 4vw, 3.1rem);
      letter-spacing: -0.06em;
      margin: 8px 0;
    }

    .dynamic-fields {
      display: grid;
      gap: 14px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-top: 22px;
    }

    label {
      display: grid;
      gap: 8px;
    }

    label span {
      color: var(--ink);
      font-weight: 950;
    }

    label small,
    label em {
      color: var(--muted);
      font-size: 12px;
      font-style: normal;
      line-height: 1.45;
    }

    input,
    select,
    textarea {
      background: color-mix(in srgb, var(--paper) 78%, white 8%);
      border: 1px solid var(--border);
      border-radius: 18px;
      color: var(--ink);
      font: inherit;
      font-weight: 800;
      outline: none;
      padding: 13px 14px;
      width: 100%;
    }

    .file-field {
      grid-column: 1 / -1;
    }

    .review-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-top: 22px;
    }

    .review-grid div,
    .empty-step {
      background: color-mix(in srgb, var(--paper) 62%, transparent);
      border: 1px solid var(--border);
      border-radius: 22px;
      padding: 16px;
    }

    .review-grid small {
      color: var(--muted);
      display: block;
      font-weight: 900;
      margin-bottom: 6px;
    }

    .review-grid strong {
      color: var(--ink);
    }

    .step-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      justify-content: space-between;
      margin-top: 26px;
    }

    .step-actions button {
      border: 1px solid var(--border);
      border-radius: 999px;
      cursor: pointer;
      font: inherit;
      font-weight: 950;
      padding: 12px 18px;
    }

    .step-actions .primary {
      background: var(--ink);
      color: var(--paper);
    }

    .step-actions .ghost {
      background: transparent;
      color: var(--ink);
    }

    .step-actions .back-action {
      margin-right: auto;
    }

    .step-actions .danger {
      color: var(--accent);
    }

    .notice {
      border-radius: 18px;
      font-weight: 900;
      padding: 12px 14px;
    }

    .success {
      background: rgb(44 170 116 / 14%);
      color: #1b7a53;
    }

    .error {
      background: rgb(220 59 99 / 14%);
      color: #b42b55;
    }

    .step-skeleton {
      display: grid;
      gap: 14px;
      margin-top: 24px;
    }

    .step-skeleton i {
      animation: pulse 1.2s ease-in-out infinite;
      background: color-mix(in srgb, var(--ink) 10%, transparent);
      border-radius: 24px;
      height: 92px;
    }

    @keyframes pulse {
      50% { opacity: .45; }
    }

    @media (max-width: 860px) {
      .landing-hero,
      .seo-content,
      .earning-strip,
      .steps-grid,
      .step-layout {
        grid-template-columns: 1fr;
      }

      .steps-nav {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        position: static;
      }
    }

    @media (max-width: 580px) {
      .dynamic-fields,
      .review-grid,
      .steps-nav {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class SellerOnboardingPage {
  protected readonly auth = inject(AuthService);
  protected readonly i18n = inject(I18nService);
  private readonly onboarding = inject(SellerOnboardingService);
  private readonly sellerPanel = inject(SellerPanelService);
  private readonly fortuneCategories = inject(FortuneCategoryService);
  private readonly seo = inject(SeoService);
  private readonly router = inject(Router);
  protected readonly site = inject(SiteConfigService);
  protected readonly fortuneBrand = computed(() => this.site.experience('fortune'));
  protected readonly instantOnboarding = computed(() => this.site.config().sellerOnboarding?.mode === 'instant');

  // SSR/istemci birleşiminde API payload'ı gelmeden varsayılan 5 adımın
  // görünmesini engelle. Gerçek adımlar yüklenene kadar iskelet gösterilir.
  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly payload = signal<SellerOnboardingPayload>({});
  protected readonly currentStepKey = signal<SellerOnboardingStepKey>('preferences');
  protected readonly notice = signal('');
  protected readonly error = signal('');
  protected readonly fieldErrors = signal<Record<string, string>>({});
  protected readonly postOnboardingStep = signal<string | null>(null);
  protected readonly postProfileUpload = signal<Record<string, unknown> | null>(null);
  protected readonly postProfilePreview = signal('');
  protected readonly postProfileUploading = signal(false);
  protected readonly postProfileError = signal('');
  protected readonly uploadingField = signal('');
  protected readonly stepOrder: SellerOnboardingStepKey[] = ['preferences', 'branding', 'payment', 'documents', 'review'];
  protected formData: Record<string, any> = {};
  protected readonly shopNameState = signal('');
  protected readonly shopNameValid = signal(false);
  private instantStartPending = false;
  protected instantShopName = '';
  protected readonly instantStage = signal<'name' | 'setup'>('name');
  protected readonly instantCategoriesLoading = signal(false);
  protected readonly instantCategories = signal<FortuneSaleCategory[]>([]);
  protected readonly instantProfilePreview = signal('');
  private instantProfileFile: File | null = null;
  private readonly instantDrafts = new Map<number, InstantOfferDraft>();
  protected onboardingCopy(): {
    eyebrow: string; title: string; lead: string; back: string; continue: string;
    complete: string; saving: string; uploading: string;
  } {
    const copy = {
      tr: {
        eyebrow: this.fortuneBrand() ? 'Falcı kaydı' : 'Satıcı kaydı',
        title: this.fortuneBrand() ? 'Falcı hesabını adım adım oluştur.' : 'Satıcı hesabını adım adım oluştur.',
        lead: 'Her adım kaydedilir. İstersen önceki adımlara dönerek bilgilerini güncelleyebilirsin.',
        back: 'Geri', continue: 'Kaydet ve devam et', complete: 'Başvuruyu tamamla', saving: 'Kaydediliyor...', uploading: 'Belge yükleniyor...'
      },
      en: {
        eyebrow: this.fortuneBrand() ? 'Reader application' : 'Seller application',
        title: this.fortuneBrand() ? 'Create your reader profile step by step.' : 'Create your seller profile step by step.',
        lead: 'Each step is saved. You can return to earlier steps and update your information at any time.',
        back: 'Back', continue: 'Save and continue', complete: 'Submit application', saving: 'Saving...', uploading: 'Uploading document...'
      },
      de: {
        eyebrow: this.fortuneBrand() ? 'Beraterbewerbung' : 'Verkäuferbewerbung',
        title: this.fortuneBrand() ? 'Erstelle dein Beraterprofil Schritt für Schritt.' : 'Erstelle dein Verkäuferprofil Schritt für Schritt.',
        lead: 'Jeder Schritt wird gespeichert. Frühere Schritte kannst du jederzeit öffnen und bearbeiten.',
        back: 'Zurück', continue: 'Speichern und weiter', complete: 'Bewerbung absenden', saving: 'Wird gespeichert...', uploading: 'Dokument wird hochgeladen...'
      },
      es: {
        eyebrow: this.fortuneBrand() ? 'Solicitud de vidente' : 'Solicitud de vendedor',
        title: this.fortuneBrand() ? 'Crea tu perfil de vidente paso a paso.' : 'Crea tu perfil de vendedor paso a paso.',
        lead: 'Cada paso se guarda. Puedes volver a los pasos anteriores y actualizar tus datos cuando quieras.',
        back: 'Atrás', continue: 'Guardar y continuar', complete: 'Enviar solicitud', saving: 'Guardando...', uploading: 'Subiendo documento...'
      },
      fr: {
        eyebrow: this.fortuneBrand() ? 'Candidature de voyant' : 'Candidature vendeur',
        title: this.fortuneBrand() ? 'Créez votre profil de voyant étape par étape.' : 'Créez votre profil vendeur étape par étape.',
        lead: 'Chaque étape est enregistrée. Vous pouvez revenir aux étapes précédentes pour modifier vos informations.',
        back: 'Retour', continue: 'Enregistrer et continuer', complete: 'Envoyer la candidature', saving: 'Enregistrement...', uploading: 'Téléversement du document...'
      }
    };
    return copy[this.i18n.lang() as keyof typeof copy] ?? copy.en;
  }

  protected stepDefinition(stepKey: SellerOnboardingStepKey): { title: string; description: string } {
    const copy: Record<string, Record<string, { title: string; description: string }>> = {
      tr: {
        preferences: { title: 'Tercihler', description: 'Dil, ülke ve çalışma tercihlerini belirle.' },
        branding: { title: this.fortuneBrand() ? 'Falcı profili' : 'Satıcı profili', description: this.fortuneBrand() ? 'Falcı adını, profil bilgilerini ve başvuru kimliğini oluştur.' : 'Satıcı adını, profil bilgilerini ve başvuru kimliğini oluştur.' },
        payment: { title: 'Ödeme ve bilgiler', description: 'Ödeme almak ve yasal süreci tamamlamak için gerekli bilgileri gir.' },
        documents: { title: 'Belgeler', description: 'Başvuru için istenen belge ve bilgileri tamamla.' },
        review: { title: 'Kontrol ve gönderim', description: 'Başvuru bilgilerini kontrol et ve başvurunu gönder.' }
      },
      en: {
        preferences: { title: 'Preferences', description: 'Set your language, country and working preferences.' },
        branding: { title: this.fortuneBrand() ? 'Reader profile' : 'Seller profile', description: this.fortuneBrand() ? 'Create your reader name, profile details and application identity.' : 'Create your seller name, profile details and application identity.' },
        payment: { title: 'Payment and details', description: 'Enter the information needed for payments and legal requirements.' },
        documents: { title: 'Documents', description: 'Complete the documents and information required for your application.' },
        review: { title: 'Review and submit', description: 'Review your application details and submit your application.' }
      },
      de: {
        preferences: { title: 'Einstellungen', description: 'Lege Sprache, Land und Arbeitspräferenzen fest.' },
        branding: { title: 'Beraterprofil', description: 'Erstelle deinen Beraternamen und deine Profilinformationen.' },
        payment: { title: 'Zahlung und Angaben', description: 'Gib die für Zahlungen und rechtliche Anforderungen nötigen Angaben ein.' },
        documents: { title: 'Dokumente', description: 'Vervollständige die für deine Bewerbung erforderlichen Dokumente.' },
        review: { title: 'Prüfen und absenden', description: 'Prüfe deine Angaben und sende die Bewerbung ab.' }
      },
      es: {
        preferences: { title: 'Preferencias', description: 'Configura tu idioma, país y preferencias de trabajo.' },
        branding: { title: 'Perfil de vidente', description: 'Crea tu nombre de vidente y los datos de tu perfil.' },
        payment: { title: 'Pagos y datos', description: 'Introduce los datos necesarios para los pagos y los requisitos legales.' },
        documents: { title: 'Documentos', description: 'Completa los documentos y datos necesarios para tu solicitud.' },
        review: { title: 'Revisar y enviar', description: 'Revisa los datos de tu solicitud y envíala.' }
      },
      fr: {
        preferences: { title: 'Préférences', description: 'Définissez votre langue, votre pays et vos préférences de travail.' },
        branding: { title: 'Profil de voyant', description: 'Créez votre nom de voyant et les informations de votre profil.' },
        payment: { title: 'Paiement et informations', description: 'Saisissez les informations nécessaires aux paiements et aux obligations légales.' },
        documents: { title: 'Documents', description: 'Complétez les documents et informations nécessaires à votre candidature.' },
        review: { title: 'Vérifier et envoyer', description: 'Vérifiez vos informations et envoyez votre candidature.' }
      }
    };
    return copy[this.i18n.lang()]?.[stepKey] ?? copy['en'][stepKey] ?? { title: stepKey, description: '' };
  }

  protected stepTitle(step: SellerOnboardingStep | null | undefined): string {
    const stepKey = String(step?.stepKey ?? this.currentStepKey());
    const definition = this.stepDefinition(stepKey);
    const title = String(step?.title ?? '').trim();
    return title && title !== stepKey ? title : (definition.title || stepKey);
  }
  protected formHints(): Record<string, string> {
    return this.shopNameState()
      ? { shopName: this.shopNameState() }
      : { shopName: 'Latin harfleri, rakam, nokta, tire ve alt çizgi kullanılabilir.' };
  }
  protected localizedLanding(): {
    eyebrow: string; title: string; lead: string; benefits: string[]; start: string;
    authTitle: string; authLead: string; highlights: Array<{ title: string; text: string }>;
    why: string; whyTitle: string; whyText: string; stepsTitle: string; steps: string[];
  } {
    const copy = {
      en: {
        eyebrow: 'Fal Deryası reader application',
        title: 'Turn your intuition into a professional online practice.',
        lead: 'Offer coffee, tarot, dream and energy readings through a trusted profile with live sessions, secure payments and discoverable service pages.',
        benefits: ['Earn from anywhere', 'Live sessions and gifts', 'Secure credit payments', 'Search-friendly reader profile'],
        start: 'Start your application', authTitle: 'Sign in to become a reader',
        authLead: 'Continue securely with your Aymudo account. You can create an account on the same screen.',
        highlights: [
          { title: 'Offer online readings', text: 'Publish your expertise and services in a clear digital showcase.' },
          { title: 'Grow your audience', text: 'Bring your profile, reviews, posts and live sessions together.' },
          { title: 'Manage your income', text: 'Follow services, customer activity and earnings from one dashboard.' }
        ],
        why: 'Why Fal Deryası?', whyTitle: 'Profile, payment, reviews and live sessions in one place.',
        whyText: 'Build a lasting professional presence where clients can discover your expertise, compare services and pay securely.',
        stepsTitle: 'Complete your application in three steps', steps: ['Create or sign in to your account', 'Prepare your reader profile', 'Submit your profile for review']
      },
      de: {
        eyebrow: 'Bewerbung als Berater bei Fal Deryası',
        title: 'Mache aus deiner Intuition eine professionelle Online-Beratung.',
        lead: 'Biete Kaffeeorakel, Tarot, Traum- und Energiedeutungen über ein vertrauenswürdiges Profil mit Live-Sitzungen, sicheren Zahlungen und auffindbaren Angebotsseiten an.',
        benefits: ['Ortsunabhängig verdienen', 'Live-Sitzungen und Geschenke', 'Sichere Credit-Zahlungen', 'Suchmaschinenfreundliches Profil'],
        start: 'Bewerbung starten', authTitle: 'Anmelden und Berater werden',
        authLead: 'Fahre sicher mit deinem Aymudo-Konto fort. Ein neues Konto kannst du direkt im selben Fenster erstellen.',
        highlights: [
          { title: 'Online beraten', text: 'Präsentiere deine Fachgebiete und Angebote übersichtlich.' },
          { title: 'Reichweite aufbauen', text: 'Vereine Profil, Bewertungen, Beiträge und Live-Sitzungen.' },
          { title: 'Einnahmen verwalten', text: 'Behalte Angebote, Kundenvorgänge und Einnahmen im Blick.' }
        ],
        why: 'Warum Fal Deryası?', whyTitle: 'Profil, Zahlungen, Bewertungen und Live-Sitzungen an einem Ort.',
        whyText: 'Schaffe einen dauerhaften professionellen Auftritt, über den Kunden dich finden, Angebote vergleichen und sicher bezahlen können.',
        stepsTitle: 'Bewerbung in drei Schritten', steps: ['Konto erstellen oder anmelden', 'Beraterprofil vorbereiten', 'Profil zur Prüfung einreichen']
      },
      es: {
        eyebrow: 'Solicitud de vidente en Fal Deryası',
        title: 'Convierte tu intuición en una práctica online profesional.',
        lead: 'Ofrece lecturas de café, tarot, sueños y energía con un perfil de confianza, sesiones en directo, pagos seguros y páginas visibles.',
        benefits: ['Gana desde cualquier lugar', 'Directos y regalos', 'Pagos seguros con créditos', 'Perfil optimizado para buscadores'],
        start: 'Inicia tu solicitud', authTitle: 'Inicia sesión para ser vidente',
        authLead: 'Continúa de forma segura con tu cuenta Aymudo. También puedes crear una cuenta en la misma pantalla.',
        highlights: [
          { title: 'Ofrece lecturas online', text: 'Publica tu experiencia y servicios en un escaparate claro.' },
          { title: 'Amplía tu audiencia', text: 'Reúne perfil, reseñas, publicaciones y directos.' },
          { title: 'Gestiona tus ingresos', text: 'Controla servicios, clientes e ingresos desde un panel.' }
        ],
        why: '¿Por qué Fal Deryası?', whyTitle: 'Perfil, pagos, reseñas y sesiones en directo en un solo lugar.',
        whyText: 'Crea una presencia profesional donde los clientes puedan encontrarte, comparar servicios y pagar con seguridad.',
        stepsTitle: 'Completa tu solicitud en tres pasos', steps: ['Crea una cuenta o inicia sesión', 'Prepara tu perfil de vidente', 'Envía el perfil para revisión']
      },
      fr: {
        eyebrow: 'Candidature de voyant sur Fal Deryası',
        title: 'Transformez votre intuition en activité professionnelle en ligne.',
        lead: 'Proposez des lectures de café, tarot, rêves et énergie avec un profil fiable, des consultations en direct et des paiements sécurisés.',
        benefits: ['Gagnez où que vous soyez', 'Lives et cadeaux', 'Paiements sécurisés en crédits', 'Profil optimisé pour la recherche'],
        start: 'Commencer la candidature', authTitle: 'Connectez-vous pour devenir voyant',
        authLead: 'Continuez avec votre compte Aymudo en toute sécurité. Vous pouvez aussi créer un compte sur le même écran.',
        highlights: [
          { title: 'Proposez des consultations', text: 'Présentez clairement vos spécialités et vos services.' },
          { title: 'Développez votre audience', text: 'Réunissez profil, avis, publications et consultations en direct.' },
          { title: 'Gérez vos revenus', text: 'Suivez services, clients et revenus depuis un seul espace.' }
        ],
        why: 'Pourquoi Fal Deryası ?', whyTitle: 'Profil, paiements, avis et consultations au même endroit.',
        whyText: 'Créez une présence professionnelle durable où les clients peuvent vous trouver, comparer vos services et payer en toute sécurité.',
        stepsTitle: 'Votre candidature en trois étapes', steps: ['Créez un compte ou connectez-vous', 'Préparez votre profil de voyant', 'Envoyez votre profil pour validation']
      }
    };
    return copy[this.i18n.lang() as keyof typeof copy] ?? copy.en;
  }

  protected readonly steps = computed<SellerOnboardingStep[]>(() => {
    const steps = this.payload().steps ?? [];
    if (steps.length) {
      return steps;
    }
    return this.stepOrder.map((stepKey, index): SellerOnboardingStep => ({
      stepKey,
      title: this.stepLabel(stepKey),
      description: this.stepDescription(stepKey),
      displayOrder: index + 1,
      formFields: []
    }));
  });

  protected readonly currentStep = computed(() => this.steps().find((step) => step.stepKey === this.currentStepKey()) ?? null);
  protected readonly currentFields = computed(() => ((this.currentStep()?.formFields ?? []) as SellerOnboardingField[])
    .filter((field) => Number(field.showDisplay ?? 1) !== 0 && this.fieldConditionMatches(field)));

  constructor() {
    if (this.instantOnboarding()) {
      this.seo.setPage({
        title: `Falcı Ol | ${this.site.config().name}`,
        description: `${this.site.config().name} üzerinde form doldurmadan falcı hesabını aç ve fal türlerini satışa çıkar.`,
        path: this.i18n.routePath('becomeSeller'),
        breadcrumbs: [{ name: 'Ana sayfa', path: '/' }, { name: 'Falcı ol', path: this.i18n.routePath('becomeSeller') }]
      });
      void this.auth.refresh().then(() => {
        this.loading.set(false);
        const user=this.auth.user();
        this.instantShopName=String(user?.seller?.shopName || [user?.name,user?.surname].filter(Boolean).join(' ') || '').trim();
        if (Number(user?.seller?.id ?? user?.seller?.sellerID ?? 0) > 0) void this.loadInstantSellerSetup();
      });
      return;
    }
    if (!this.fortuneBrand()) {
      this.seo.setPage({
        title: `Satıcı Ol | ${this.site.config().name}'da Mağazanı Aç`,
        description: `${this.site.config().name}'da el emeği ve özgün ürünlerini satışa çıkar; güvenli ödeme, SEO uyumlu mağaza ve kolay sipariş yönetimiyle markanı büyüt.`,
        path: this.i18n.routePath('becomeSeller'),
        breadcrumbs: [{ name: 'Ana sayfa', path: '/' }, { name: 'Satıcı ol', path: this.i18n.routePath('becomeSeller') }]
      });
      void this.auth.refresh().then(() => this.load());
      return;
    }
    this.seo.setPage({
      title: this.i18n.t('sellerOnboardingSeoTitle'),
      description: this.i18n.t('sellerOnboardingSeoDescription'),
      path: this.i18n.routePath('becomeSeller'),
      breadcrumbs: [
        { name: this.i18n.t('home'), path: this.i18n.routePath('home') },
        { name: this.i18n.t('becomeSeller'), path: this.i18n.routePath('becomeSeller') }
      ],
      schema: this.i18n.lang() === 'tr' ? [
        {
          '@type': 'Service',
          name: 'Fal Deryası Falcı Başvurusu',
          serviceType: 'Online falcı profili ve dijital satış vitrini',
          provider: {
            '@type': 'Organization',
            name: 'Fal Deryası'
          },
          areaServed: 'TR',
          description: 'Online fal bakarak gelir elde etmek isteyen falcılar için profil, ödeme, canlı yayın ve yorum altyapısı.'
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Fal Deryası uzerinde online falcı olmak için ne gerekir?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Aymudo hesabıyla giriş yapıp falcı başvuru adımlarını tamamlamak gerekir. Profil bilgileri, uzmanlık alanları ve gerekli başvuru alanları onay sürecinde değerlendirilir.'
              }
            },
            {
              '@type': 'Question',
              name: 'Online fal bakarak ne kadar kazanılır?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Kazanç garanti edilmez; profil kalitesi, hizmet fiyatı, müşteri talebi, yorumlar ve canlı yayın etkileşimi gibi değişkenlere göre farklılık gösterir.'
              }
            },
            {
              '@type': 'Question',
              name: 'Hangi fal türleriyle başvuru yapılabilir?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Kahve falı, tarot, rüya yorumu, astroloji, enerji okuması ve platform tarafından desteklenen diğer danışmanlık alanlarıyla başvuru yapılabilir.'
              }
            }
          ]
        }
      ] : [
        {
          '@type': 'Service',
          name: this.localizedLanding().eyebrow,
          serviceType: this.localizedLanding().title,
          provider: {
            '@type': 'Organization',
            name: 'Fal Deryası'
          },
          description: this.i18n.t('sellerOnboardingSeoDescription')
        }
      ]
    });
    void this.auth.refresh().then(() => this.load());
  }

  protected async startInstantOnboarding(): Promise<void> {
    if (this.instantStartPending || !this.auth.loggedIn()) return;
    if (!this.instantShopName.trim()) { this.error.set('Mağaza adını yaz.'); return; }
    if (!/^[A-Za-z0-9._-]{2,100}$/.test(this.instantShopName.trim())) { this.error.set('Mağaza adında yalnızca Latin harfleri, rakam, nokta, tire ve alt çizgi kullan.'); return; }
    this.instantStartPending = true;
    this.saving.set(true);
    this.error.set('');
    try {
      const result = await this.onboarding.quickStart({shopName:this.instantShopName.trim()});
      if (!result.result) throw new Error(result.message || 'Falcı hesabı açılamadı.');
      await this.auth.refresh(true);
      await this.loadInstantSellerSetup();
    } catch (error: any) {
      this.error.set(error?.error?.message || error?.message || 'Falcı hesabı açılamadı.');
    } finally {
      this.instantStartPending = false;
      this.saving.set(false);
    }
  }

  protected instantDraft(categoryID:number):InstantOfferDraft {
    if (!this.instantDrafts.has(categoryID)) this.instantDrafts.set(categoryID,{categoryID,description:'',creditPrice:null,active:false});
    return this.instantDrafts.get(categoryID)!;
  }

  protected instantPriceOptions(category:FortuneSaleCategory):number[] {
    const min=Math.ceil(Number(category.minCreditPrice || 50)/50)*50;
    const max=Math.floor(Number(category.maxCreditPrice || 1000)/50)*50;
    const prices:number[]=[];
    for(let price=min;price<=max;price+=50) prices.push(price);
    return prices;
  }

  protected instantActiveCategoryCount():number {
    return this.instantCategories().filter(category=>this.instantDraft(category.categoryID).active).length;
  }

  protected async publishInstantSeller():Promise<void> {
    if (this.saving()) return;
    const selected=this.instantCategories().filter(category=>this.instantDraft(category.categoryID).active);
    if (!selected.length) { this.error.set('Yayına çıkmak için en az bir fal türünü aç.'); return; }
    for (const category of selected) {
      const draft=this.instantDraft(category.categoryID);
      if (!draft.description.trim()) { this.error.set(`${category.title} için kısa bir fal açıklaması yaz.`); return; }
      if (!draft.creditPrice) { this.error.set(`${category.title} için kredi fiyatı seç.`); return; }
    }
    this.saving.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      if (this.instantProfileFile) {
        const upload=await this.sellerPanel.uploadSellerProfileAsset('avatar',this.instantProfileFile);
        await this.sellerPanel.saveSellerProfile({shopName:this.instantShopName.trim(),profilePictureUploads:[upload]});
      }
      const changed=this.instantCategories().filter(category=>{
        const draft=this.instantDraft(category.categoryID);
        return draft.active || !!draft.offerID;
      });
      for (const category of changed) await this.fortuneCategories.sellerSave(this.instantDraft(category.categoryID) as unknown as Record<string,unknown>);
      this.notice.set('Profilin ve seçtiğin fallar yayına alındı.');
      await this.auth.refresh(true);
      await this.router.navigateByUrl(`${this.i18n.routePath('sellerDashboard')}/fortune-sales`);
    } catch (error:any) {
      this.error.set(error?.error?.message || error?.message || 'Fallar yayına alınamadı.');
    } finally {
      this.saving.set(false);
    }
  }

  private async loadInstantSellerSetup():Promise<void> {
    this.instantStage.set('setup');
    this.instantCategoriesLoading.set(true);
    this.error.set('');
    try {
      const data=await this.fortuneCategories.sellerData();
      this.instantCategories.set(data.categories || []);
      const shopName=String(data.seller?.shopName || '').trim();
      if (shopName) this.instantShopName=shopName;
      for (const offer of data.offers || []) this.instantDrafts.set(Number(offer.categoryID),{
        offerID:Number(offer.offerID),categoryID:Number(offer.categoryID),description:String(offer.description || ''),
        creditPrice:Number(offer.creditPrice || 0) || null,active:!!offer.active
      });
    } catch (error:any) {
      this.error.set(error?.error?.message || error?.message || 'Fal kategorileri yüklenemedi.');
    } finally {
      this.instantCategoriesLoading.set(false);
    }
  }

  protected selectInstantProfileImage(event:Event):void{
    const input=event.target as HTMLInputElement;
    const file=input.files?.[0] || null;
    if(!file){this.instantProfileFile=null;this.instantProfilePreview.set('');return;}
    if(file.size>5*1024*1024){this.instantProfileFile=null;this.instantProfilePreview.set('');this.error.set('Profil resmi en fazla 5 MB olabilir.');input.value='';return;}
    this.instantProfileFile=file;
    this.instantProfilePreview.set(URL.createObjectURL(file));
    this.error.set('');
  }

  protected firstStepKey(): SellerOnboardingStepKey {
    return this.steps()[0]?.stepKey ?? 'preferences';
  }

  protected openStep(stepKey: SellerOnboardingStepKey): void {
    if (stepKey === 'completed') {
      return;
    }
    this.currentStepKey.set(stepKey);
    this.notice.set('');
    this.error.set('');
    this.fieldErrors.set({});
  }

  protected previousStep(): void {
    const index = this.stepIndex(this.currentStepKey());
    if (index > 0) {
      this.openStep(this.steps()[index - 1].stepKey);
    }
  }

  protected async saveCurrentStep(): Promise<void> {
    const stepKey = this.currentStepKey();
    const nextStep = this.nextStepKey(stepKey);
    this.saving.set(true);
    this.notice.set('');
    this.error.set('');
    try {
      if (Object.prototype.hasOwnProperty.call(this.formData, 'shopName')) {
        await this.checkShopName();
        if (!this.shopNameValid()) {
          this.error.set(this.shopNameState() || 'Mağaza adı kullanılamıyor.');
          return;
        }
      }
      const result = await this.onboarding.saveStep(stepKey, nextStep, this.formData);
      this.mergeProgress(result);
      this.currentStepKey.set((result.currentStep as SellerOnboardingStepKey) || nextStep);
      this.notice.set('Adım kaydedildi.');
      this.scrollTop();
    } catch (error) {
      if (error instanceof SellerOnboardingSaveError) {
        this.fieldErrors.set(error.fieldErrors);
      }
      const message=error instanceof Error ? error.message : '';
      this.error.set(message && message !== 'Islem tamamlanamadi.'
        ? message
        : 'Adım kaydedilemedi. IBAN dahil zorunlu alanları kontrol edip tekrar deneyelim.');
    } finally {
      this.saving.set(false);
    }
  }

  protected async checkShopName(): Promise<void> {
    const shopName = String(this.formData['shopName'] || '').trim();
    if (!shopName) {
      this.shopNameValid.set(false);
      this.shopNameState.set('Mağaza adı zorunlu.');
      return;
    }
    try {
      const result = await this.onboarding.checkShopName(shopName);
      this.shopNameValid.set(result.result);
      this.shopNameState.set(result.message || (result.result ? 'Mağaza adı kullanılabilir.' : 'Mağaza adı kullanılamıyor.'));
      if (result.result && result.shopName) {
        this.formData['shopName'] = result.shopName;
      }
    } catch {
      this.shopNameValid.set(false);
      this.shopNameState.set('Mağaza adı kontrol edilemedi.');
    }
  }

  protected onFormFieldBlur(field: DynamicFormField): void {
    if (this.fieldKey(field as SellerOnboardingField) === 'shopName') {
      void this.checkShopName();
    }
  }

  protected onFormUploadComplete(event: { key: string; uploads: unknown[] }): void {
    this.fieldErrors.update((errors) => {
      const next = { ...errors };
      delete next[event.key];
      return next;
    });
    this.notice.set(`${event.uploads.length} dosya yüklendi.`);
  }

  protected onFormUploadError(event: { key: string; message: string }): void {
    this.fieldErrors.update((errors) => ({ ...errors, [event.key]: event.message }));
    this.error.set(event.message);
  }

  protected async complete(): Promise<void> {
    this.saving.set(true);
    this.notice.set('');
    this.error.set('');
    try {
      const latest = await this.onboarding.get();
      this.payload.set(latest);
      this.formData = { ...(latest.formData ?? {}) };
      const missingStep = (latest.steps ?? []).find((step) => step.isRequired && !step.isCompleted);
      if (missingStep) {
        this.currentStepKey.set(missingStep.stepKey);
        this.error.set(`${missingStep.title || this.stepLabel(missingStep.stepKey)} adımını tamamlayıp tekrar deneyelim.`);
        this.scrollTop();
        return;
      }
      const result = await this.onboarding.complete();
      if (!result.result) {
        const stepKey = result.missingStep || result.currentStep;
        if (stepKey) {
          this.currentStepKey.set(stepKey);
        }
        this.mergeProgress(result);
        this.error.set(result.message || 'Eksik başvuru adımlarını tamamlayıp tekrar deneyelim.');
        this.scrollTop();
        return;
      }
      this.mergeProgress(result);
      const nextPostStep=result.postOnboarding?.completed ? null : (result.postOnboarding?.currentStep ?? 'profileImage');
      this.postOnboardingStep.set(nextPostStep);
      this.currentStepKey.set(nextPostStep ?? 'completed');
      this.notice.set(nextPostStep
        ? 'Başvurun tamamlandı. Şimdi profilini ve ilk falını birkaç adımda hazırlayabilirsin.'
        : (result.awaitingApproval === false
          ? (this.fortuneBrand() ? 'Falcı kaydın tamamlandı. Artık falcılık yapabilirsin.' : 'Satıcı kaydın tamamlandı. Artık ürünlerini satışa açabilirsin.')
          : (this.fortuneBrand() ? 'Falcılık başvurun alındı. Onaylandığında sana bildireceğiz.' : 'Satıcı başvurun alındı. Onaylandığında sana bildireceğiz.')));
      await this.auth.refresh(true);
    } catch (error) {
      const message=error instanceof Error ? error.message : '';
      this.error.set(message && message !== 'Islem tamamlanamadi.'
        ? message
        : 'Başvuru tamamlanamadı. Eksik veya geçersiz adımları kontrol edelim.');
    } finally {
      this.saving.set(false);
    }
  }

  protected async selectPostProfileImage(event: Event): Promise<void> {
    const input=event.target as HTMLInputElement;
    const file=input.files?.[0];
    input.value='';
    if(!file || this.postProfileUploading()) return;
    if(file.size>5*1024*1024){
      this.postProfileError.set('Görsel en fazla 5MB olabilir.');
      return;
    }
    this.postProfileUploading.set(true);
    this.postProfileError.set('');
    try{
      const upload=await this.sellerPanel.uploadSellerProfileAsset('avatar',file);
      this.postProfileUpload.set(upload);
      this.postProfilePreview.set(URL.createObjectURL(file));
    }catch(error){
      this.postProfileError.set(error instanceof Error ? error.message : 'Profil görseli yüklenemedi.');
    }finally{
      this.postProfileUploading.set(false);
    }
  }

  protected async savePostProfileImage(): Promise<void> {
    const upload=this.postProfileUpload();
    if(!upload || this.saving()) return;
    this.saving.set(true);
    this.postProfileError.set('');
    try{
      const profile=await this.sellerPanel.sellerProfile();
      await this.sellerPanel.saveSellerProfile({
        shopName:String(profile.shopName || this.auth.user()?.seller?.shopName || '').trim(),
        profilePictureUploads:[upload]
      });
      await this.completePostStep('profileImage');
    }catch(error){
      this.postProfileError.set(error instanceof Error ? error.message : 'Profil görseli kaydedilemedi.');
    }finally{
      this.saving.set(false);
    }
  }

  protected async onFirstProductSaved(): Promise<void> {
    this.notice.set('Falın kaydedildi. İstersen listeden başka bir fal daha hazırlayabilirsin.');
    this.scrollTop();
  }

  protected async skipPostStep(): Promise<void> {
    const step=this.postOnboardingStep();
    if(step) await this.completePostStep(step);
  }

  private async completePostStep(stepKey: string): Promise<void> {
    if(stepKey!=='profileImage') this.saving.set(true);
    this.error.set('');
    try{
      const result=await this.onboarding.savePostStep(stepKey);
      const next=result.postOnboarding?.completed ? null : (result.postOnboarding?.currentStep ?? null);
      this.postOnboardingStep.set(next);
      this.payload.update(payload=>({...payload,postOnboarding:result.postOnboarding}));
      this.notice.set(next ? 'Tamamlandı. Sıradaki adıma geçiyoruz.' : 'Profilin ve ilk satış adımın hazır. Artık panelinden devam edebilirsin.');
      this.postProfileUpload.set(null);
      this.postProfilePreview.set('');
      await this.auth.refresh();
      this.scrollTop();
    }catch(error){
      this.error.set(error instanceof Error ? error.message : 'Başlangıç adımı kaydedilemedi.');
    }finally{
      if(stepKey!=='profileImage') this.saving.set(false);
    }
  }

  protected async reset(): Promise<void> {
    this.saving.set(true);
    try {
      await this.onboarding.reset();
      await this.load();
      this.currentStepKey.set('preferences');
      this.notice.set('Başvuru adımları sıfırlandı.');
    } finally {
      this.saving.set(false);
    }
  }

  protected fieldKey(field: SellerOnboardingField): string {
    return String(field.formKey || `field_${field.id || 'x'}`);
  }

  protected fieldLabel(field: SellerOnboardingField): string {
    return String(field.title || field.formKey || 'Alan');
  }

  protected isRequired(field: SellerOnboardingField): boolean {
    return field.required === true || Number(field.required ?? 0) === 1;
  }

  protected inputType(field: SellerOnboardingField): string {
    const type = String(field.type || 'text').toLowerCase();
    if (['email', 'mail'].includes(type)) {
      return 'email';
    }
    if (['phone', 'tel'].includes(type)) {
      return 'tel';
    }
    if (['number', 'price', 'decimal'].includes(type)) {
      return 'number';
    }
    if (['date', 'datetime-local', 'time', 'password'].includes(type)) {
      return type;
    }
    return 'text';
  }

  protected isLongTextField(field: SellerOnboardingField): boolean {
    return ['textarea', 'longtext', 'textArea'].includes(String(field.type || ''));
  }

  protected isFileField(field: SellerOnboardingField): boolean {
    return ['image', 'picture', 'file', 'media', 'gallery', 'video'].includes(String(field.type || '').toLowerCase());
  }

  protected isMultiFileField(field: SellerOnboardingField): boolean {
    return ['gallery', 'media'].includes(String(field.type || '').toLowerCase());
  }

  protected fieldOptions(field: SellerOnboardingField): Array<{ value: string; label: string }> {
    const options = field.options;
    if (Array.isArray(options)) {
      return options.map((option) => this.optionRecord(option));
    }
    if (options && typeof options === 'object') {
      return Object.entries(options as Record<string, unknown>).map(([value, label]) => ({
        value,
        label: String(label)
      }));
    }
    return [];
  }

  protected isCompleted(stepKey: SellerOnboardingStepKey): boolean {
    return (this.payload().completedSteps ?? []).includes(stepKey);
  }

  protected stepIndex(stepKey: SellerOnboardingStepKey): number {
    const index = this.steps().findIndex((step) => step.stepKey === stepKey);
    return index >= 0 ? index : 0;
  }

  protected stepLabel(stepKey: SellerOnboardingStepKey): string {
    return this.stepDefinition(stepKey).title;
  }

  protected stepDescription(stepKey: SellerOnboardingStepKey): string {
    return this.stepDefinition(stepKey).description;
  }

  protected isSeller(): boolean {
    const seller=this.auth.user()?.seller;
    return Number(this.payload().sellerID ?? seller?.id ?? seller?.sellerID ?? 0)>0;
  }

  protected reviewEntries(): Array<{ key: string; label: string; value: string }> {
    const fields = new Map<string, SellerOnboardingField>();
    for (const step of this.steps()) {
      for (const field of (step.formFields ?? []) as SellerOnboardingField[]) {
        fields.set(this.fieldKey(field), field);
      }
    }
    return Object.entries(this.formData)
      .filter(([key, value]) => fields.has(key) && value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key,
        label: this.fieldLabel(fields.get(key) ?? { formKey: key }),
        value: this.reviewValue(fields.get(key), value)
      }));
  }

  private reviewValue(field: SellerOnboardingField | undefined, value: unknown): string {
    if (Array.isArray(value)) {
      return `${value.length} dosya`;
    }
    const option = this.fieldOptions(field ?? {}).find((item) => item.value === String(value));
    return option?.label ?? String(value);
  }

  private fieldConditionMatches(field: SellerOnboardingField): boolean {
    const key = String(field.conditionFieldKey ?? '').trim();
    if (!key) {
      return true;
    }
    const actual = String(this.formData[key] ?? '');
    const expected = String(field.conditionValue ?? '');
    switch (String(field.conditionOperator ?? 'equals')) {
      case 'not_equals': return actual !== expected;
      case 'filled': return actual !== '';
      case 'empty': return actual === '';
      case 'in': return expected.split(',').map((item) => item.trim()).includes(actual);
      case 'not_in': return !expected.split(',').map((item) => item.trim()).includes(actual);
      default: return actual === expected;
    }
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    try {
      // Login/register can finish immediately before the auth signal is
      // refreshed on a route navigation. Do not leave the page permanently
      // on its skeleton in that short window.
      if (!this.auth.loggedIn()) {
        await this.auth.refresh(true);
      }
      if (!this.auth.loggedIn()) {
        this.error.set(this.fortuneBrand() ? 'Falcı kaydı için giriş yapmalısın.' : 'Satıcı kaydı için giriş yapmalısın.');
        return;
      }
      const payload = await this.onboarding.get();
      this.payload.set(payload);
      this.formData = { ...(payload.formData ?? {}) };
      if (!this.postOnboardingStep() && !this.payload().postOnboarding?.completed) {
        this.postOnboardingStep.set(
          payload.postOnboarding && !payload.postOnboarding.completed
            ? (payload.postOnboarding.currentStep ?? 'profileImage')
            : null
        );
      }
      this.currentStepKey.set((payload.currentStep as SellerOnboardingStepKey) || this.firstStepKey());
    } catch {
      this.error.set(this.fortuneBrand() ? 'Falcı kayıt adımları yüklenemedi.' : 'Satıcı kayıt adımları yüklenemedi.');
    } finally {
      this.loading.set(false);
    }
  }

  private nextStepKey(stepKey: SellerOnboardingStepKey): SellerOnboardingStepKey {
    const index = this.stepIndex(stepKey);
    return this.steps()[index + 1]?.stepKey ?? 'review';
  }

  private mergeProgress(progress: SellerOnboardingPayload): void {
    if (!Object.keys(progress).length) {
      return;
    }
    this.payload.update((payload) => ({
      ...payload,
      ...progress,
      steps: payload.steps,
      formData: this.formData,
      completedSteps: (progress.completedSteps ?? payload.completedSteps ?? []) as SellerOnboardingStepKey[]
    }));
  }

  private optionRecord(option: unknown): { value: string; label: string } {
    if (option && typeof option === 'object') {
      const record = option as Record<string, unknown>;
      const value = String(record['value'] ?? record['id'] ?? record['key'] ?? record['code'] ?? '');
      return {
        value,
        label: String(record['label'] ?? record['title'] ?? record['name'] ?? value)
      };
    }
    return {
      value: String(option),
      label: String(option)
    };
  }

  private scrollTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
